import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn.ts'
import { Spinner } from './spinner.tsx'

type Variant = 'primary' | 'secondary' | 'ghost' | 'link'
type Size = 'md' | 'sm'

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand text-white font-semibold hover:bg-brand-dark disabled:opacity-70',
  secondary: 'border border-line-strong bg-surface text-ink font-medium hover:bg-canvas',
  ghost: 'text-ink-muted hover:bg-surface-hover hover:text-ink',
  link: 'text-brand hover:text-brand-dark p-0 h-auto',
}

const SIZES: Record<Size, string> = {
  md: 'px-4 py-3 text-[15px]',
  sm: 'px-3 py-2 text-[13.5px]',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: ReactNode
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-control transition-colors disabled:cursor-not-allowed',
        VARIANTS[variant],
        variant !== 'link' && SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner className="size-4" /> : icon}
      {children}
    </button>
  )
}
